import { useMemo, useCallback, useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { Loader2 } from "lucide-react";

import { BookingBreadcrumbs } from "@features/booking/components/BookingBreadcrumbs";
import { BookingPageLayout } from "@features/booking/layouts";
import { useLanguage } from "@core/i18n";
import { useBooking } from "@features/booking/state";
import { useRestaurant } from "@core/tenant/TenantProvider";
import { useAuth } from "@core/auth/AuthProvider";
import { useCalendarAvailability } from "@features/booking/hooks/useCalendarAvailability";
import { useTimeSlots } from "@features/booking/hooks/useTimeSlots";
import { useBookingFlow } from "@features/booking/flow";
import { useAnalytics, ANALYTICS_EVENTS } from "@core/analytics";
import { CALENDAR_DAY_TYPE_REQUEST } from "@features/booking/types/calendar";
import { computeVisibleServices } from "@features/booking/utils/serviceVisibility";
import { resolveServiceTypesForDate } from "@features/booking/utils/availability";
import type { ServiceType } from "@features/booking/state/types";

const SelectServicePage = () => {
  const { t } = useTranslation('booking');
  const restaurant = useRestaurant();
  const { authState } = useAuth();
  const { goToNext } = useBookingFlow();
  const { trackEvent } = useAnalytics();

  useEffect(() => {
    trackEvent({ name: ANALYTICS_EVENTS.BOOKING_SELECT_SERVICE });
  }, [trackEvent]);

  const booking = useBooking();
  const {
    diners,
    selectedDate,
    setService,
    setTimeSlots,
    setSlotTerms,
    setColumnType,
    specialEvent,
    meitreNetworkMenuId,
  } = booking;

  // Deferred navigation: goToNext() must run AFTER setTimeSlots is committed
  // to React state so the flow context has the correct menuCount.
  const [pendingNavigation, setPendingNavigation] = useState(false);
  useEffect(() => {
    if (pendingNavigation && booking.timeSlots) {
      setPendingNavigation(false);
      goToNext();
    }
  }, [pendingNavigation, booking.timeSlots, goToNext]);

  const allServiceTypes = (restaurant.serviceTypes || []) as ServiceType[];

  const { getDateAvailability, isLoading: isCalendarLoading } = useCalendarAvailability({
    restaurantId: restaurant.id,
    partySize: diners,
    serviceTypes: allServiceTypes,
    enabled: diners > 0,
    meitreNetworkMenuId,
    preSelectedMenuId: booking.preSelectedMenuId,
    hasPreferredAccess: !!authState.preferred,
    areaFilterIds: booking.areaFilterIds,
  });

  // See resolveServiceTypesForDate's docblock for the no-dead-end fallback
  // policy. Gated on !isCalendarLoading: while the calendar is still
  // in-flight, getDateAvailability(selectedDate) returns undefined
  // indistinguishably from "zero services for the filtered area" — firing
  // the fallback during that window would enable useTimeSlots for the full
  // service list (wasted search-all-hours requests) and briefly let the user
  // click an excluded service. Keep the list empty until the calendar
  // resolves; useTimeSlots stays disabled (enabled: availableServiceTypes.length > 0).
  const availableServiceTypes = useMemo(() => {
    if (!selectedDate || isCalendarLoading) return [];
    const dayAvailability = getDateAvailability(selectedDate);
    return resolveServiceTypesForDate(dayAvailability, allServiceTypes, !!booking.areaFilterIds?.length);
  }, [selectedDate, isCalendarLoading, getDateAvailability, booking.areaFilterIds, allServiceTypes]);

  const {
    slotsByService,
    servicesWithSlots,
    isLoading: isSlotsLoading,
    getServiceSlots,
  } = useTimeSlots({
    restaurantId: restaurant.id,
    partySize: diners,
    date: selectedDate,
    serviceTypes: availableServiceTypes,
    enabled: availableServiceTypes.length > 0,
    hasPreferredAccess: !!authState.preferred,
    meitreNetworkMenuId,
    areaFilterIds: booking.areaFilterIds,
  });

  const handleServiceSelect = useCallback((serviceType: ServiceType) => {
    const slots = getServiceSlots(serviceType);
    const response = slotsByService[serviceType];
    setService(serviceType);
    setTimeSlots(slots);
    setSlotTerms(response?.center?.termsAndConditions ?? []);
    setColumnType(response?.center?.type ?? null);

    // Detect preferred-only column: hasPreferred flag set, all available slots are preferred, no normal slots
    const rawSlots = response?.center?.slots ?? [];
    const hasPreferred = response?.center?.hasPreferred;
    const thereArePreferred = rawSlots.some(s => s.preferred && s.type !== 'No');
    const thereAreNormal = rawSlots.some(s => !s.preferred && s.type !== 'No');
    // Update isEarlyAccessFlow using per-service calendar day type
    // (search-all-hours does NOT return "request" type — only calendar-availability-new does)
    const dayAvail = selectedDate ? getDateAvailability(selectedDate) : null;
    const serviceDayType = dayAvail?.services?.[serviceType]?.type;
    const isEarlyAccess = serviceDayType === CALENDAR_DAY_TYPE_REQUEST;

    booking.updateBooking({
      centerHasOnlyPreferred: !!hasPreferred && thereArePreferred && !thereAreNormal,
      isEarlyAccessFlow: isEarlyAccess,
    });

    // Defer navigation: state updates are batched, so goToNext() here would
    // use a stale flow context (old menuCount). Setting this flag triggers
    // navigation via useEffect after React commits the new timeSlots.
    setPendingNavigation(true);
  }, [getServiceSlots, slotsByService, setService, setTimeSlots, setSlotTerms, setColumnType, booking, selectedDate, getDateAvailability]);

  return (
    <BookingPageLayout
      title={t('SERVICE_TITLE')}
      subtitle={t('SERVICE_SUBTITLE')}
      subtitleClassName="text-foreground/50 text-sm mb-6"
      contentClassName=""
      breadcrumbs={<BookingBreadcrumbs show={['DINERS', 'DATE']} />}
    >
      {/* Loading state. Includes isCalendarLoading: while the calendar is in
          flight the availableServiceTypes memo returns [] (see its comment),
          which keeps useTimeSlots disabled — and a disabled query reports
          isLoading=false. Without this the page would flash the "no services"
          empty state on a cold cache before swapping to the real buttons. */}
      {(isSlotsLoading || isCalendarLoading) && (
        <div className="flex items-center justify-center py-12">
          <Loader2 className="w-8 h-8 animate-spin text-brand" />
        </div>
      )}

      {/* Service buttons */}
      {!isSlotsLoading && !isCalendarLoading && (() => {
        const visibleServices = computeVisibleServices({
          services: servicesWithSlots,
          eventServiceTypes: specialEvent?.serviceTypes,
        });

        return (
          <div className="w-full space-y-3">
            {visibleServices.map((serviceData) => {
              const { service } = serviceData;

              return (
                <button
                  key={service}
                  data-testid={`service-btn-${service}`}
                  onClick={() => handleServiceSelect(service)}
                  className="w-full h-[90px] rounded-xl border flex flex-col items-center justify-center transition-all duration-200 shadow-sm border-border translucent:border-surface-border bg-card hover:border-brand hover:border-opacity-70 cursor-pointer"
                >
                  <span className="text-xl font-medium text-foreground">
                    {t(`FILTER_STRING_${service.toUpperCase()}`)}
                  </span>
                </button>
              );
            })}

            {visibleServices.length === 0 && (
              <div className="text-center py-8 text-foreground/50">
                {t('SERVICE_NO_SERVICES_AVAILABLE')}
              </div>
            )}
          </div>
        );
      })()}
    </BookingPageLayout>
  );
};

export default SelectServicePage;
